import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import type { SecurityDepositBreakdown } from "../../pages/refund-management/editRefundDepositBreakdown";

function formatBreakdownAmount(amount: number | null | undefined): string {
  const n =
    amount == null || !Number.isFinite(Number(amount)) ? 0 : Number(amount);
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export interface SecurityDepositBreakdownCardProps {
  breakdown: SecurityDepositBreakdown;
  subtitle?: string;
  className?: string;
}

const SecurityDepositBreakdownCard: React.FC<
  SecurityDepositBreakdownCardProps
> = ({
  breakdown,
  subtitle = "Payment and refund summary for this booking",
  className = "mb-4 shadow-sm border-0 rounded-3",
}) => {
  const rows = [
    {
      label: "Total amount",
      value: formatBreakdownAmount(breakdown.totalPayment),
    },
    {
      label: "Proposed refundable amount",
      value: formatBreakdownAmount(breakdown.proposedRefundAmount),
    },
    breakdown.isRefundProcessed
      ? {
          label: "Amount refunded",
          value: formatBreakdownAmount(breakdown.amountRefunded),
        }
      : {
          label: "Refundable amount",
          value: formatBreakdownAmount(breakdown.refundableAmount),
        },
  ] as const;

  return (
    <Paper className={className} sx={{ border: "1px solid #e0e0e0" }}>
      <Box className="px-4 pt-4 pb-4">
        <Typography
          variant="h6"
          className="fw-bold mb-1"
          sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
        >
          Security Deposit Breakdown
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          {subtitle}
        </Typography>
        <Box
          sx={{
            borderRadius: 1,
            border: "1px solid #e9ecef",
            bgcolor: "#fafafa",
            overflow: "hidden",
          }}
        >
          {rows.map((row, index) => (
            <Box
              key={row.label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 2,
                px: 2,
                py: 1.5,
                borderBottom:
                  index < rows.length - 1 ? "1px solid #eee" : "none",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                {row.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
        {breakdown.breakdownCaption ? (
          <Typography
            variant="caption"
            sx={{
              color: breakdown.breakdownCaption.color,
              display: "block",
              mt: 1.5,
            }}
          >
            {breakdown.breakdownCaption.text}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
};

export default SecurityDepositBreakdownCard;
